import React, { FC, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { ChatReply } from "./ChatReply";
import { ChatAttachment } from "./ChatAttachment";
import { useChatStore } from "../../../stores/ChatStore";
import { formatText } from "../../../functions/general";
import { ChatGroupMessage } from "../../../interfaces/Chat/Chat";
import { returnIncomingCall, returnMaps, returnMissedCall } from "../../General/ChatElement";
import { ChatGroupAction } from "./ChatGroupAction";

interface ChatGroupMessageProps {
    message: ChatGroupMessage
    isAction: boolean
    scrollToChat: (index: number) => void
}

const ChatGroupMessageBubble: FC<ChatGroupMessageProps> = ({ message, isAction, scrollToChat }) => {
    const intl = useIntl()

    // Check if message is from me
    const isFromMe = message.fromMe
    // Align message to right if message is from me
    const alignItems = isFromMe ? 'align-items-end' : 'align-items-start'

    const { listChatGroupParticipants, isOpenCheckBoxGroupForward, listChatGroupRooms, groupID, selectedChatPhone } = useChatStore()
    const [showMessageActions, setShowMessageActions] = useState<boolean>(false)
    const colorMessage = listChatGroupParticipants[message.waSender.phone].color

    const handleContextMenu = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        await new Promise(resolve => setTimeout(resolve, 0))
        const messageActionsDropdown = document.getElementById('message-actions-dropdown')
        messageActionsDropdown?.click()
    }

    return (
        <div
            className={`d-flex flex-column ${alignItems} my-2`}
            key={message.id}
            id={message.id ?? ''}
            onMouseEnter={() => setShowMessageActions(true)}
            onMouseLeave={() => setShowMessageActions(false)}
            onContextMenu={handleContextMenu}
        >
            <span className="d-flex justify-content-end">
                {message.sender?.name}
            </span>
            {
                showMessageActions && !isOpenCheckBoxGroupForward && isAction && selectedChatPhone.length === 1 &&
                <ChatGroupAction message={message} />
            }
            <div className="mt-2 p-3 rounded bg-light-primary text-gray-800 fw-bolder fs-6 mw-250px mw-lg-400px overflow-auto">
                {
                    !isFromMe && <span className="fs-7" style={{ color: colorMessage, fontWeight: "700" }}>{message.waSender.name}</span>
                }
                {/* Forward Label */}
                {message.isForwarded && (
                    <div className={`d-flex align-items-center gap-1 mb-1 ${isFromMe ? 'justify-content-end' : 'justify-content-start'}`}>
                        <i className="fas fa-share fs-7 text-gray-500 me-2"></i>
                        <span className="text-gray-500 fs-7" style={{ fontStyle: 'italic' }}>{intl.formatMessage({ id: "FORM.LABEL.FORWARDED" })}</span>
                    </div>
                )}
                <ChatReply message={message} scrollToChat={scrollToChat} />
                {
                    message.id && message.attachmentName &&
                    <ChatAttachment message={message} />
                }
                {
                    message.message === '{{MISSED CALL}}' || message.message === '{{MISSED VOICE CALL}}' ? returnMissedCall(message, intl) :
                        (message.message === '{{INCOMING CALL}}' || message.message === '{{ACCEPTED CALL}}') ? returnIncomingCall(message, intl) :
                            message.message.includes('[User sends location]') ? returnMaps(message, listChatGroupParticipants, listChatGroupRooms[groupID], intl, "chetMessage") :
                                <div dangerouslySetInnerHTML={{ __html: formatText(message.message, listChatGroupParticipants, listChatGroupRooms[groupID], false, true) }} className='fw-normal' />
                }
                <div className="text-gray-400 fw-bold fs-7 float-end align-items-center d-flex">
                    {
                        message.updatedAt &&
                        <i className="me-2"><small>{intl.formatMessage({ id: "FORM.LABEL.EDITED" })}</small></i>
                    }
                    <div>
                        {moment(message.receivedAt).format('HH:mm')}
                    </div>
                    {
                        message.fromMe &&
                        <div className="ms-3">
                            <i className="fas fa-check-double"></i>
                        </div>
                    }
                </div>
            </div>
        </div >
    )
}

const areEqual = (prevProps: ChatGroupMessageProps, nextProps: ChatGroupMessageProps) => {
    return (
        prevProps.message.id === nextProps.message.id &&
        prevProps.message.message === nextProps.message.message &&
        prevProps.message.updatedAt === nextProps.message.updatedAt &&
        prevProps.message.action?.isEditing === nextProps.message.action?.isEditing
    );
};

const MemoizedChatMessageBubble = React.memo(ChatGroupMessageBubble, areEqual);

export { MemoizedChatMessageBubble as ChatGroupMessageBubble }